import React, { useState } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ChevronDown, ChevronRight } from 'lucide-react';

const fmtMin = (m) => (m == null ? '--' : `${Math.floor(m / 60)}h${String(Math.round(m % 60)).padStart(2, '0')}`);
const STATUS = { completed: ['Terminé', 'bg-emerald-100 text-emerald-700'], active: ['Présent', 'bg-blue-100 text-blue-700'], on_break: ['En pause', 'bg-amber-100 text-amber-700'] };

const Time = ({ v, cls }) => <td className={`py-3 px-3 text-sm font-mono ${v ? cls : 'text-slate-300'}`}>{v || '--:--'}</td>;

const Detail = ({ entry, bd }) => {
  const deducted = (bd.punched_break_minutes || 0) + (bd.auto_deduction_minutes || 0);
  return (
    <tr className="bg-slate-50 border-b border-slate-200" data-testid={`timesheet-detail-${entry.id}`}>
      <td colSpan={10} className="px-6 py-4">
        <div className="grid md:grid-cols-3 gap-6 text-sm">
          <div>
            <p className="text-xs font-bold uppercase text-slate-500 mb-2">Timbrages</p>
            <ul className="space-y-1 font-mono">
              <li>Arrivée <b className="text-emerald-700">{bd.arrival || '--:--'}</b></li>
              {bd.breaks.map((b, i) => (
                <li key={i}>{b.lunch ? 'Midi' : 'Pause'} <b className="text-amber-700">{b.start}</b> → <b className="text-amber-700">{b.end || 'en cours'}</b> <span className="text-slate-500">({b.minutes ?? '…'} min)</span></li>
              ))}
              <li>Départ <b className="text-red-700">{bd.departure || '--:--'}</b></li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-bold uppercase text-slate-500 mb-2">Déductions</p>
            <ul className="space-y-1">
              <li>Pauses timbrées : <b>{bd.punched_break_minutes} min</b></li>
              {bd.auto_deductions.map((d, i) => <li key={i}>{d.label} : <b>{d.minutes} min</b></li>)}
              {!bd.auto_deductions.length && <li className="text-slate-400">Aucune déduction automatique</li>}
            </ul>
          </div>
          <div>
            <p className="text-xs font-bold uppercase text-slate-500 mb-2">Calcul</p>
            <ul className="space-y-1">
              <li>Présence (arrivée → départ) : <b>{fmtMin(bd.gross_minutes)}</b></li>
              <li>Total déduit : <b>{fmtMin(deducted)}</b></li>
              <li className="text-base">Heures travaillées : <b data-testid={`timesheet-net-${entry.id}`}>{entry.total_hours != null ? `${entry.total_hours} h (${fmtMin(entry.total_hours * 60)})` : '--'}</b></li>
            </ul>
          </div>
        </div>
      </td>
    </tr>
  );
};

export const TimesheetRow = ({ entry, name }) => {
  const [open, setOpen] = useState(false);
  const bd = entry.breakdown || { breaks: [], auto_deductions: [] };
  const [label, cls] = STATUS[entry.status] || ['En attente', 'bg-slate-100 text-slate-700'];
  return (
    <>
      <tr className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setOpen(o => !o)} data-testid={`timesheet-row-${entry.id}`}>
        <td className="py-3 px-2 text-slate-400">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
        <td className="py-3 px-3 text-sm text-slate-900 font-medium">{name}</td>
        <td className="py-3 px-3 text-sm text-slate-700 whitespace-nowrap">{entry.date ? format(new Date(entry.date), 'EEE d MMM yyyy', { locale: fr }) : '--'}</td>
        <Time v={bd.arrival} cls="text-emerald-600" />
        <Time v={bd.lunch_out} cls="text-amber-600" />
        <Time v={bd.lunch_in} cls="text-amber-600" />
        <Time v={bd.departure} cls="text-red-600" />
        <td className="py-3 px-3 text-sm font-mono text-slate-600">{(bd.punched_break_minutes || 0) + (bd.auto_deduction_minutes || 0)} min</td>
        <td className="py-3 px-3 text-sm font-mono text-slate-900 font-bold">{entry.total_hours ?? '--'}</td>
        <td className="py-3 px-3"><span className={`inline-block px-2 py-1 text-xs font-medium rounded-md ${cls}`}>{label}</span></td>
      </tr>
      {open && <Detail entry={entry} bd={bd} />}
    </>
  );
};
